import { sectionViewDef, type Crop2D, type Vec3, type View2DDef } from './lib'

// ---------------------------------------------------------------------------
// Corte desenhado sobre uma planta (fluxo clássico do desenho técnico).
//
// O utilizador clica dois pontos, P1 e P2, sobre a planta. O plano de
// corte é o plano VERTICAL que contém esse segmento.
//
// CONVENÇÃO DA DIRECÇÃO: olha-se para a ESQUERDA do sentido P1 -> P2.
// Com a base da planta (right = Este, up = Norte), andar para Este tem
// o Norte à esquerda: rodar a direcção do traço +90° no sentido
// anti-horário no plano do desenho dá a direcção de observação.
//
// Propriedade que sai de graça e vale a pena registar: com essa
// convenção, o eixo `right` do corte fica EXACTAMENTE alinhado com
// P1 -> P2. Verificação (mundo three, x = Este, y = Cima, z = -Norte),
// com d = (dx, 0, dz) unitário:
//
//   viewDir = (dz, 0, -dx)                       (d rodado +90° em planta)
//   right   = viewDir × up = (dx, 0, dz) = d     (up = (0,1,0))
//
// Logo o desenho do corte tem P1 em x = 0 e P2 em x = |P2 - P1|: a
// leitura da esquerda para a direita é a mesma do traço desenhado na
// planta. Invertendo a direcção, `right` passa a ser -d e P2 cai em
// x negativo; o `crop` é recalculado por projecção, nunca assumido.
//
// A origem do corte fica à cota da BASE do modelo, para o desenho ter
// o pavimento em y = 0 e crescer para cima.
//
// Puro: números a entrar, `View2DDef` a sair.
// ---------------------------------------------------------------------------

/** Profundidade por omissão de um corte desenhado, em metros. */
export const DRAWN_SECTION_DEFAULT_DEPTH = 3

export interface SectionFromLineOptions {
  /** Ponto inicial do traço, em coordenadas de MUNDO. */
  p1: Vec3
  /** Ponto final do traço, em coordenadas de MUNDO. */
  p2: Vec3
  /** Cota mínima do modelo (base do desenho). */
  minY: number
  /** Cota máxima do modelo (topo do desenho). */
  maxY: number
  name: string
  id?: string
  depth?: number
  /** Inverte a direcção de observação (olha para a direita de P1->P2). */
  flip?: boolean
  scaleDenom?: number
  /** Folga vertical acrescentada acima e abaixo, em metros. */
  paddingY?: number
}

/** Direcção de observação de um traço desenhado em planta: a
 *  perpendicular horizontal, virada para a ESQUERDA de P1 -> P2
 *  (ou para a direita, com `flip`). `null` quando os dois pontos
 *  coincidem em planta. */
export function viewDirFromPlanLine(
  p1: Vec3,
  p2: Vec3,
  flip = false,
): Vec3 | null {
  const dx = p2[0] - p1[0]
  const dz = p2[2] - p1[2]
  const len = Math.hypot(dx, dz)
  if (!Number.isFinite(len) || len < 1e-6) return null
  const ux = dx / len
  const uz = dz / len
  // Rodar +90° no plano do desenho da planta (right = Este, up = Norte)
  // corresponde, no mundo three, a (ux, uz) -> (uz, -ux).
  return flip ? [-uz, 0, ux] : [uz, 0, -ux]
}

/** Constrói a vista de corte a partir de dois pontos picados na planta.
 *  `null` quando o traço é degenerado (dois cliques no mesmo sítio). */
export function sectionViewFromLine(
  opts: SectionFromLineOptions,
): View2DDef | null {
  const viewDir = viewDirFromPlanLine(opts.p1, opts.p2, opts.flip)
  if (!viewDir) return null
  const padY = opts.paddingY ?? 0.5
  const baseY = Math.min(opts.minY, opts.maxY) - padY
  const topY = Math.max(opts.minY, opts.maxY) + padY
  const origin: Vec3 = [opts.p1[0], baseY, opts.p1[2]]

  let def: View2DDef
  try {
    def = sectionViewDef({
      id: opts.id,
      name: opts.name,
      origin,
      viewDir,
      depth: Math.max(0.01, opts.depth ?? DRAWN_SECTION_DEFAULT_DEPTH),
      scaleDenom: opts.scaleDenom,
    })
  } catch {
    return null
  }

  // Janela: projectar P1 e P2 no eixo `right` em vez de assumir o sinal.
  // Assim o `flip` fica tratado sem casos especiais.
  const projX = (p: Vec3): number =>
    (p[0] - origin[0]) * def.right[0] +
    (p[1] - origin[1]) * def.right[1] +
    (p[2] - origin[2]) * def.right[2]
  const x1 = projX(opts.p1)
  const x2 = projX(opts.p2)
  const crop: Crop2D = {
    minX: Math.min(x1, x2),
    maxX: Math.max(x1, x2),
    minY: 0,
    maxY: topY - baseY,
  }
  def.crop = crop
  return def
}

/** Inverte a direcção de observação de um corte já criado, mantendo o
 *  plano. O `crop` é recalculado por reflexão em x (o eixo `right`
 *  troca de sinal), o que preserva a janela desenhada. */
export function flipSectionView(view: View2DDef): View2DDef {
  const right: Vec3 = [-view.right[0], -view.right[1], -view.right[2]]
  const crop = view.crop
    ? {
        minX: -view.crop.maxX,
        maxX: -view.crop.minX,
        minY: view.crop.minY,
        maxY: view.crop.maxY,
      }
    : undefined
  return { ...view, right, crop }
}

/** Nomes sequenciais do desenho técnico: "Corte A-A", "Corte B-B", …
 *  Salta os que já existem. Depois do Z continua com AA-AA. */
export function nextSectionMarkName(usedNames: Iterable<string>): string {
  const used = new Set(usedNames)
  for (let i = 0; i < 26 * 27; i++) {
    const letter = sequenceLetter(i)
    const candidate = `Corte ${letter}-${letter}`
    if (!used.has(candidate)) return candidate
  }
  return `Corte ${Date.now().toString(36).toUpperCase()}`
}

/** 0 -> 'A', 25 -> 'Z', 26 -> 'AA', 27 -> 'AB', … */
export function sequenceLetter(index: number): string {
  const A = 'A'.charCodeAt(0)
  if (index < 26) return String.fromCharCode(A + index)
  const first = Math.floor(index / 26) - 1
  const second = index % 26
  return (
    String.fromCharCode(A + first) + String.fromCharCode(A + second)
  )
}
